import { useEffect, useId, useState } from "react";
import { useGetCoursesQuery } from "./store/hooks";
import { CoreModal, CoreSearchField, CoreSearchFieldPaper, CoreSpinner } from "@verity/components/core";
import i18n from "../../../i18n";
import styles from "./LivePopInPage.module.css";
import CompositeTable, {
	CompositeTableBodyRow,
	CompositeTableCell,
	CompositeTableHeaderRow,
	CompositeTableNoResults,
	CompositeTablePaginationWrapper,
} from "../../../components/composite-table";

export default function SelectCourseModal({
	onClose,
	instituteId,
	onSelect,
}: {
	onClose: () => void;
	instituteId: string;
	onSelect: (courseId: string) => void;
}) {
	const [page, setPage] = useState(0);
	const [searchText, setSearchText] = useState("");

	useEffect(() => {
		setPage(0);
	}, [searchText]);

	const { data, isError, isLoading } = useGetCoursesQuery({ instituteId, page, name: searchText });

	const isLoaded = !isLoading && !isError;
	const ariaControlsId = useId();

	const selectCourse = (courseId: string) => {
		onSelect(courseId);
		onClose();
	};

	return (
		<CoreModal
			open
			onClose={onClose}
			title={i18n.t("portal.components.livePopIn.livePopInPageTSX.selectCourse")}
			noActions
		>
			<div className={styles.selectCourseModalBody}>
				<CoreSearchFieldPaper>
					<CoreSearchField
						label={i18n.t("portal.components.livePopIn.livePopInPageTSX.searchCourse")}
						onSearch={setSearchText}
						totalResults={data?.courses.length ?? 0}
						ariaControls={ariaControlsId}
						value={searchText}
					/>
				</CoreSearchFieldPaper>
				{isLoading && <CoreSpinner />}
				{isError && (
					<CompositeTableNoResults>
						{i18n.t("portal.components.livePopIn.livePopInPageTSX.coursesError")}
					</CompositeTableNoResults>
				)}
				{isLoaded && data && data.courses.length === 0 && (
					<CompositeTableNoResults>
						{i18n.t("portal.components.livePopIn.livePopInPageTSX.noCourses")}
					</CompositeTableNoResults>
				)}
				{isLoaded && data && data.courses.length > 0 && (
					<CompositeTablePaginationWrapper
						className={styles.selectCourseTable}
						page={page}
						onChange={setPage}
						count={data.totalPages}
					>
						<CompositeTable
							id={ariaControlsId}
							head={
								<CompositeTableHeaderRow
									columns={[
										{
											key: "code",
											content: i18n.t("portal.components.livePopIn.livePopInPageTSX.courseCode"),
										},
										{
											key: "name",
											content: i18n.t("portal.components.livePopIn.livePopInPageTSX.courseName"),
										},
									]}
								/>
							}
						>
							{data.courses.map(({ id, code, name }) => (
								<CompositeTableBodyRow key={id} onClick={() => selectCourse(id)}>
									<CompositeTableCell>
										{code || i18n.t("portal.components.livePopIn.livePopInPageTSX.noCode")}
									</CompositeTableCell>
									<CompositeTableCell>
										{name || i18n.t("portal.components.livePopIn.livePopInPageTSX.noName")}
									</CompositeTableCell>
								</CompositeTableBodyRow>
							))}
						</CompositeTable>
					</CompositeTablePaginationWrapper>
				)}
			</div>
		</CoreModal>
	);
}
